import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type {
  NotificationChannel,
  NotificationRouteRequest,
  NotificationRouteResponse,
  NotificationTemplateRequest,
  NotificationTemplateResponse,
  TemplatePreviewResponse,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';

/**
 * What the clinic's patients are told, and on which channel (FR-1002, DEC-043).
 *
 * Two tabs because they are two different jobs. Wording is edited per event, per channel, per
 * language, and the placeholders a message cannot lose are shown next to the box rather than left
 * in a manual — the save is refused without them, and being refused by a rule you were never told
 * about is the worst version of a form.
 *
 * Routing is per event, and some of it is deliberately not editable. Quiet hours are a property of
 * the event, not a setting: a clinic cannot make a one-time code wait until morning, because a
 * patient locked out of the app at nine at night does not try again at eight.
 */
const CHANNELS: readonly NotificationChannel[] = ['WHATSAPP', 'SMS', 'PUSH', 'EMAIL', 'IN_APP'];

const LOCALES = ['ar', 'en'] as const;

/**
 * The event's name in the reader's language, falling back to its key.
 *
 * The vocabulary is closed and every member is translated, so the fallback should never show. It
 * is here because the alternative — an empty cell — would be the one case where a clinic could not
 * tell which message it was editing.
 */
function eventLabel(t: TFunction, event: string | undefined): string {
  if (!event) {
    return '';
  }
  return t(`notifications.events.${event}`, { defaultValue: event });
}

export function NotificationsPage() {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const [tab, setTab] = useState<'templates' | 'routes'>('templates');

  if (!clinicId) {
    return <PageHeader title={t('nav.items.notifications')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.notifications')} description={t('notifications.description')} />

      <div className="toolbar" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'templates'}
          className={tab === 'templates' ? 'button button--primary' : 'button button--ghost'}
          onClick={() => setTab('templates')}
        >
          {t('notifications.templatesTab')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'routes'}
          className={tab === 'routes' ? 'button button--primary' : 'button button--ghost'}
          onClick={() => setTab('routes')}
        >
          {t('notifications.routesTab')}
        </button>
      </div>

      {tab === 'templates' ? <Templates clinicId={clinicId} /> : <Routes clinicId={clinicId} />}
    </>
  );
}

// ── Templates ─────────────────────────────────────────────────────────────

function Templates({ clinicId }: { readonly clinicId: string }) {
  const { t } = useTranslation();
  const canManage = useAuthStore((state) => state.permissions).has('NOTIFICATION_TEMPLATE_MANAGE');
  const [locale, setLocale] = useState<(typeof LOCALES)[number]>('ar');
  const [editing, setEditing] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [preview, setPreview] = useState<string | null>(null);

  const templates = useApiQuery<NotificationTemplateResponse[]>(
    queryKeys.notificationTemplates(clinicId, locale),
    `/clinics/${clinicId}/notifications/templates?locale=${locale}`,
  );

  const save = useApiMutation<NotificationTemplateResponse, NotificationTemplateRequest>(
    (payload) => ({
      path: `/clinics/${clinicId}/notifications/templates`,
      options: { method: 'PUT', body: payload },
    }),
    {
      invalidates: [queryKeys.notificationTemplates(clinicId, locale)],
      onSuccess: () => {
        setEditing(null);
        setPreview(null);
      },
    },
  );

  const renderPreview = useApiMutation<TemplatePreviewResponse, NotificationTemplateRequest>((payload) => ({
    path: `/clinics/${clinicId}/notifications/templates/preview`,
    options: { method: 'POST', body: payload },
  }));

  const reset = useApiMutation<NotificationTemplateResponse, NotificationTemplateResponse>(
    (template) => ({
      path:
        `/clinics/${clinicId}/notifications/templates/${template.event}/${template.channel}` +
        `/reset?locale=${locale}`,
      options: { method: 'POST' },
    }),
    { invalidates: [queryKeys.notificationTemplates(clinicId, locale)] },
  );

  const keyOf = (template: NotificationTemplateResponse) => `${template.event}|${template.channel}`;

  return (
    <>
      <div className="toolbar">
        {LOCALES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className={candidate === locale ? 'button button--primary' : 'button button--ghost'}
            onClick={() => {
              setLocale(candidate);
              setEditing(null);
            }}
          >
            {t(`notifications.locale.${candidate}`)}
          </button>
        ))}
      </div>

      <AsyncSection query={templates}>
        {(rows) => (
          <div className="panel">
            {rows.map((template) => {
              const identity = keyOf(template);
              const open = editing === identity;
              const draft: NotificationTemplateRequest = {
                event: template.event as NotificationTemplateRequest['event'],
                channel: template.channel as NotificationTemplateRequest['channel'],
                locale,
                subject: subject || undefined,
                body,
                enabled: template.enabled ?? true,
                providerTemplateName: template.providerTemplateName ?? undefined,
              };

              return (
                <article key={identity} className="list-row">
                  <header className="list-row__main">
                    <span className="list-row__title">{eventLabel(t, template.event)}</span>
                    <span className="badge">{template.channel}</span>
                    {template.isDefault ? <span className="badge">{t('notifications.usingDefault')}</span> : null}
                  </header>

                  {open ? (
                    <div className="form-grid">
                      <label className="field">
                        <span className="field__label">{t('notifications.subject')}</span>
                        <input
                          className="field__input"
                          dir={locale === 'ar' ? 'rtl' : 'ltr'}
                          maxLength={200}
                          value={subject}
                          onChange={(event) => setSubject(event.target.value)}
                        />
                      </label>

                      <label className="field">
                        <span className="field__label">{t('notifications.body')}</span>
                        <textarea
                          className="field__input"
                          dir={locale === 'ar' ? 'rtl' : 'ltr'}
                          rows={4}
                          maxLength={2000}
                          value={body}
                          onChange={(event) => setBody(event.target.value)}
                        />
                        <span className="field__hint">
                          {t('notifications.placeholders')}: {(template.placeholders ?? []).map((name) => `{{${name}}}`).join(' ')}
                        </span>
                        <MissingPlaceholders required={template.required ?? []} body={body} />
                      </label>

                      {preview ? (
                        <p className="field__hint" dir={locale === 'ar' ? 'rtl' : 'ltr'} data-testid="template-preview">
                          {preview}
                        </p>
                      ) : null}

                      {save.error ? <ErrorState error={save.error} /> : null}

                      <div className="toolbar">
                        <button
                          type="button"
                          className="button button--primary"
                          disabled={save.isPending || body.trim().length === 0}
                          onClick={() => save.mutate(draft)}
                        >
                          {t('notifications.save')}
                        </button>
                        <button
                          type="button"
                          className="button button--ghost"
                          disabled={renderPreview.isPending}
                          onClick={() =>
                            renderPreview.mutate(draft, {
                              onSuccess: (result) => setPreview(result.rendered ?? ''),
                            })
                          }
                        >
                          {t('notifications.preview')}
                        </button>
                        <button
                          type="button"
                          className="button button--ghost"
                          onClick={() => {
                            setEditing(null);
                            setPreview(null);
                          }}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="list-row__detail" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                        {template.body}
                      </p>
                      {canManage ? (
                        <div className="toolbar">
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => {
                              setEditing(identity);
                              setBody(template.body ?? '');
                              setSubject(template.subject ?? '');
                              setPreview(null);
                            }}
                          >
                            {t('notifications.edit')}
                          </button>
                          {template.isDefault ? null : (
                            <button
                              type="button"
                              className="button button--ghost"
                              onClick={() => reset.mutate(template)}
                            >
                              {t('notifications.reset')}
                            </button>
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </AsyncSection>
    </>
  );
}

/**
 * Names the message cannot work without and this draft has dropped.
 *
 * The server refuses the save either way. Saying so while the box is still open turns a rejected
 * submit into a corrected one.
 */
function MissingPlaceholders({ required, body }: { readonly required: readonly string[]; readonly body: string }) {
  const { t } = useTranslation();
  const missing = useMemo(
    () => required.filter((name) => !body.includes(`{{${name}}}`)),
    [required, body],
  );
  if (missing.length === 0) {
    return null;
  }
  return (
    <span className="field__hint" role="alert" data-testid="missing-placeholders">
      {t('notifications.missingPlaceholders')}: {missing.map((name) => `{{${name}}}`).join(' ')}
    </span>
  );
}

// ── Routing ───────────────────────────────────────────────────────────────

function Routes({ clinicId }: { readonly clinicId: string }) {
  const { t } = useTranslation();
  const canManage = useAuthStore((state) => state.permissions).has('NOTIFICATION_TEMPLATE_MANAGE');

  const routes = useApiQuery<NotificationRouteResponse[]>(
    queryKeys.notificationRoutes(clinicId),
    `/clinics/${clinicId}/notifications/routes`,
  );

  const save = useApiMutation<NotificationRouteResponse[], NotificationRouteRequest[]>(
    (body) => ({ path: `/clinics/${clinicId}/notifications/routes`, options: { method: 'PUT', body } }),
    { invalidates: [queryKeys.notificationRoutes(clinicId)] },
  );

  return (
    <AsyncSection query={routes}>
      {(rows) => (
        <div className="panel">
          {save.error ? <ErrorState error={save.error} /> : null}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('notifications.event')}</th>
                  <th scope="col">{t('notifications.channels')}</th>
                  <th scope="col">{t('notifications.quietHours')}</th>
                  <th scope="col">{t('notifications.enabled')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((route) => {
                  const channels = (route.channels ?? []) as NotificationChannel[];
                  const replace = (next: NotificationRouteRequest) =>
                    save.mutate(
                      rows.map((row) =>
                        row.event === route.event
                          ? next
                          : {
                              event: row.event as NotificationRouteRequest['event'],
                              channels: (row.channels ?? []) as NotificationChannel[],
                              enabled: row.enabled ?? true,
                            },
                      ),
                    );

                  return (
                    <tr key={route.event}>
                      <th scope="row">{eventLabel(t, route.event)}</th>
                      <td>
                        <div className="toolbar">
                          {CHANNELS.map((channel) => (
                            <label key={channel} className="field field--inline">
                              <input
                                type="checkbox"
                                checked={channels.includes(channel)}
                                disabled={!canManage || save.isPending}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? [...channels, channel]
                                    : channels.filter((candidate) => candidate !== channel);
                                  if (next.length === 0) {
                                    return;
                                  }
                                  replace({
                                    event: route.event as NotificationRouteRequest['event'],
                                    channels: next,
                                    enabled: route.enabled ?? true,
                                  });
                                }}
                              />
                              <span>{channel}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                      <td>
                        {route.respectsQuietHours ? t('notifications.held') : t('notifications.never')}
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={t('notifications.enabled')}
                          checked={route.enabled ?? true}
                          disabled={!canManage || save.isPending}
                          onChange={(event) =>
                            replace({
                              event: route.event as NotificationRouteRequest['event'],
                              channels,
                              enabled: event.target.checked,
                            })
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AsyncSection>
  );
}
